import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import client from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle2, X, Clock, Inbox, Handshake, Mail, RefreshCw, Users, UserPlus } from 'lucide-react';

const PARTNER_TYPE_LABELS = {
  distributor: 'Distributor',
  integrator: 'System Integrator',
  vad: 'VAD',
  isv: 'ISV',
};

function StatusBadge({ s }) {
  if (s === 'approved') return <Badge className="bg-emerald-400/15 text-emerald-300 border border-emerald-400/30">Approved</Badge>;
  if (s === 'rejected') return <Badge className="bg-rose-400/15 text-rose-300 border border-rose-400/30">Rejected</Badge>;
  return <Badge className="bg-amber-400/15 text-amber-300 border border-amber-400/30"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
}

const KIND_LABEL = {
  contact_request: { label: 'Contact request', color: 'bg-cyan-400/15 text-cyan-300 border-cyan-400/30' },
  user_registration: { label: 'User registration', color: 'bg-sky-400/15 text-sky-300 border-sky-400/30' },
  partner_registration: { label: 'Partner registration', color: 'bg-indigo-400/15 text-indigo-300 border-indigo-400/30' },
  deal_registered: { label: 'Deal registered', color: 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30' },
  deal_approved: { label: 'Deal approved', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  deal_rejected: { label: 'Deal rejected', color: 'bg-rose-400/15 text-rose-300 border-rose-400/30' },
  partner_approved: { label: 'Partner approved', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  partner_rejected: { label: 'Partner rejected', color: 'bg-rose-400/15 text-rose-300 border-rose-400/30' },
};

function KindBadge({ kind }) {
  const k = KIND_LABEL[kind] || { label: kind, color: 'bg-white/10 text-slate-300 border-white/20' };
  return <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border ${k.color}`}>{k.label}</span>;
}

export default function UserDashboard() {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState('deals'); // 'deals' | 'partners' | 'admins' | 'inbox'
  const [deals, setDeals] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [partners, setPartners] = useState([]);
  const [partnersError, setPartnersError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [promoteEmail, setPromoteEmail] = useState('');
  const [promoteBusy, setPromoteBusy] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { kind: 'reject'|'delete', id, name }
  const [dialogReason, setDialogReason] = useState('');

  const loadDeals = () => client.get('/deals').then((r) => setDeals(r.data)).catch(() => {});
  const loadInbox = () =>
    client.get('/admin/inbox').then((r) => setInbox(r.data)).catch(() => {});
  const loadPartners = () =>
    client.get('/admin/partners', { params: { status: 'all' } })
      .then((r) => { setPartners(r.data); setPartnersError(null); })
      .catch((err) => setPartnersError(err?.response?.status === 403 ? 'Admin access required.' : 'Could not load partners.'));

  useEffect(() => {
    if (!user) return;
    loadDeals();
    loadInbox();
    loadPartners();
  }, [user]);

  if (loading) return <div className="p-10 text-slate-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  const action = async (id, kind) => {
    setBusy(true);
    try {
      await client.post(`/deals/${id}/${kind}`);
      toast({ title: kind === 'approve' ? 'Deal approved' : 'Deal rejected' });
      loadDeals();
      loadInbox();
    } catch (err) {
      toast({ title: 'Action failed', description: err?.response?.data?.detail || '' });
    } finally {
      setBusy(false);
    }
  };

  const partnerAction = async (id, kind, reason) => {
    setBusy(true);
    try {
      await client.post(`/admin/partners/${id}/${kind}`, kind === 'reject' ? { reason } : undefined);
      toast({ title: kind === 'approve' ? 'Partner approved' : 'Partner rejected' });
      loadPartners();
      loadInbox();
    } catch (err) {
      toast({ title: 'Action failed', description: err?.response?.data?.detail || '' });
    } finally {
      setBusy(false);
    }
  };

  const deletePartner = async (id, reason) => {
    setBusy(true);
    try {
      await client.delete(`/admin/partners/${id}`, { params: reason ? { reason } : {} });
      toast({ title: 'Partner deleted' });
      loadPartners();
      loadInbox();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || '' });
    } finally {
      setBusy(false);
    }
  };

  const openConfirm = (kind, id, name) => {
    setDialogReason('');
    setConfirmDialog({ kind, id, name });
  };

  const submitConfirmDialog = () => {
    if (!confirmDialog) return;
    const { kind, id } = confirmDialog;
    if (kind === 'reject') {
      if (!dialogReason.trim()) return; // reason required to reject
      partnerAction(id, 'reject', dialogReason.trim());
    } else {
      deletePartner(id, dialogReason.trim() || undefined);
    }
    setConfirmDialog(null);
  };

  const promoteUser = async (e) => {
    e.preventDefault();
    if (!promoteEmail) return;
    setPromoteBusy(true);
    try {
      const res = await client.post('/admin/users/promote', { email: promoteEmail });
      toast({ title: 'Promoted to admin', description: `${res.data.company_email} is now an admin — takes effect on their next request, but they'll need to refresh this page to see it.` });
      setPromoteEmail('');
    } catch (err) {
      toast({ title: 'Could not promote', description: err?.response?.data?.detail || '' });
    } finally {
      setPromoteBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="text-xs font-mono uppercase tracking-widest text-cyan-300">Admin Console</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Visotonics Admin</h1>
      <p className="text-slate-400 mt-2">
        Review partner deals and every website form submission routed to <span className="text-cyan-300">contact@visotonics.com</span>.
      </p>

      {/* Tabs */}
      <div className="mt-8 flex items-center gap-2 border-b border-white/10 overflow-x-auto whitespace-nowrap">
        <button
          onClick={() => setTab('deals')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'deals' ? 'text-cyan-300 border-cyan-400' : 'text-slate-400 border-transparent hover:text-slate-200'}`}
        >
          <span className="inline-flex items-center gap-2"><Handshake className="w-4 h-4" /> Deals ({deals.length})</span>
        </button>
        <button
          onClick={() => setTab('partners')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'partners' ? 'text-cyan-300 border-cyan-400' : 'text-slate-400 border-transparent hover:text-slate-200'}`}
        >
          <span className="inline-flex items-center gap-2"><Users className="w-4 h-4" /> Partners ({partners.filter((p) => p.partner_status === 'pending').length} pending)</span>
        </button>
        <button
          onClick={() => setTab('admins')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'admins' ? 'text-cyan-300 border-cyan-400' : 'text-slate-400 border-transparent hover:text-slate-200'}`}
        >
          <span className="inline-flex items-center gap-2"><UserPlus className="w-4 h-4" /> Admins</span>
        </button>
        <button
          onClick={() => setTab('inbox')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'inbox' ? 'text-cyan-300 border-cyan-400' : 'text-slate-400 border-transparent hover:text-slate-200'}`}
        >
          <span className="inline-flex items-center gap-2"><Inbox className="w-4 h-4" /> Inbox ({inbox.length})</span>
        </button>
        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { loadDeals(); loadInbox(); loadPartners(); }}
            className="border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-slate-200 h-8"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {tab === 'deals' && (
        <div className="mt-6 rounded-xl border border-white/10 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-white/[0.03] text-slate-400 text-left">
              <tr>
                <th className="px-4 py-3">Partner</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Industry</th>
                <th className="px-4 py-3">Platform</th>
                <th className="px-4 py-3">Value</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {deals.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No deals yet.</td></tr>}
              {deals.map((d) => (
                <tr key={d.id} className="border-t border-white/5">
                  <td className="px-4 py-3">{d.partner_name}</td>
                  <td className="px-4 py-3"><div className="font-medium">{d.customer_name}</div><div className="text-xs text-slate-500">{d.customer_email}</div></td>
                  <td className="px-4 py-3 text-slate-300">{d.industry}</td>
                  <td className="px-4 py-3 text-slate-300">{d.platform}</td>
                  <td className="px-4 py-3 text-slate-300">${(d.estimated_value || 0).toLocaleString()}</td>
                  <td className="px-4 py-3"><StatusBadge s={d.status} /></td>
                  <td className="px-4 py-3">
                    {d.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button size="sm" disabled={busy} onClick={() => action(d.id, 'approve')} className="bg-emerald-400 hover:bg-emerald-300 text-slate-900 h-8"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve</Button>
                        <Button size="sm" disabled={busy} variant="outline" onClick={() => action(d.id, 'reject')} className="border-rose-400/40 text-rose-300 hover:bg-rose-400/10 h-8"><X className="w-3.5 h-3.5 mr-1" />Reject</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'partners' && (
        <div className="mt-6 rounded-xl border border-white/10 overflow-hidden">
          {partnersError && (
            <div className="p-10 text-center text-slate-500">
              {partnersError}
              {partnersError.includes('Admin') && (
                <div className="mt-2 text-xs text-slate-600">Partner approval requires a real admin account (role: "admin"), set directly in the database — a regular login isn't enough.</div>
              )}
            </div>
          )}
          {!partnersError && (
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-slate-400 text-left">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Partner type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Applied</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No partner accounts yet.</td></tr>}
                {partners.map((p) => (
                  <tr key={p.id} className="border-t border-white/5">
                    <td className="px-4 py-3">{p.name}</td>
                    <td className="px-4 py-3 text-slate-300">{p.company || '-'}</td>
                    <td className="px-4 py-3 text-slate-300">{p.company_email}</td>
                    <td className="px-4 py-3 text-slate-300">{PARTNER_TYPE_LABELS[p.partner_type] || '—'}</td>
                    <td className="px-4 py-3"><StatusBadge s={p.partner_status} /></td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(p.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {p.partner_status === 'pending' && (
                          <>
                            <Button size="sm" disabled={busy} onClick={() => partnerAction(p.id, 'approve')} className="bg-emerald-400 hover:bg-emerald-300 text-slate-900 h-8"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve</Button>
                            <Button size="sm" disabled={busy} variant="outline" onClick={() => openConfirm('reject', p.id, p.name)} className="border-rose-400/40 text-rose-300 hover:bg-rose-400/10 h-8"><X className="w-3.5 h-3.5 mr-1" />Reject</Button>
                          </>
                        )}
                        <Button size="sm" disabled={busy} variant="outline" onClick={() => openConfirm('delete', p.id, p.name)} className="border-white/20 text-slate-400 hover:bg-white/5 h-8">Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'admins' && (
        <div className="mt-6 max-w-lg rounded-xl border border-white/10 bg-white/[0.02] p-6">
          <div className="text-sm font-medium text-slate-200">Promote a user to admin</div>
          <p className="text-xs text-slate-500 mt-1">
            The account must already exist (they need to have registered or logged in at least once).
            Takes effect immediately server-side; they just need to refresh any open page to see it.
          </p>
          <form onSubmit={promoteUser} className="mt-4 flex gap-2">
            <Input
              type="email"
              required
              placeholder="name@company.com"
              value={promoteEmail}
              onChange={(e) => setPromoteEmail(e.target.value)}
              className="bg-black/30 border-white/10"
            />
            <Button type="submit" disabled={promoteBusy} className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 shrink-0">
              <UserPlus className="w-3.5 h-3.5 mr-1" /> Promote
            </Button>
          </form>
        </div>
      )}

      {tab === 'inbox' && (
        <div className="mt-6 space-y-3">
          {inbox.length === 0 && (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-10 text-center text-slate-500">
              <Mail className="w-6 h-6 text-slate-500 mx-auto mb-3" />
              No form submissions yet.
            </div>
          )}
          {inbox.map((m) => (
            <div key={m.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-5 hover:border-cyan-400/20 transition-colors">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <KindBadge kind={m.kind} />
                    <span className="text-xs text-slate-500">→ {m.to}</span>
                  </div>
                  <div className="font-display font-semibold text-slate-100 mt-2">{m.subject}</div>
                  <pre className="text-xs text-slate-400 mt-3 whitespace-pre-wrap font-mono leading-relaxed">
                    {m.body}
                  </pre>
                </div>
                <div className="text-[10px] font-mono text-slate-500 whitespace-nowrap">
                  {new Date(m.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <DialogContent className="bg-[#0a0d12] border-white/10 text-slate-100">
          <DialogHeader>
            <DialogTitle>
              {confirmDialog?.kind === 'delete' ? `Delete ${confirmDialog?.name}'s partner account?` : `Reject ${confirmDialog?.name}'s application?`}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {confirmDialog?.kind === 'delete'
                ? 'This is permanent. An email notice will be sent to the partner, with this reason if you provide one.'
                : 'A reason is required — it will be included in the email sent to the applicant.'}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            autoFocus
            value={dialogReason}
            onChange={(e) => setDialogReason(e.target.value)}
            placeholder={confirmDialog?.kind === 'delete' ? 'Reason (optional)' : 'Reason (required)'}
            className="bg-black/30 border-white/10"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)} className="border-white/10 bg-transparent text-slate-200 hover:bg-white/5">Cancel</Button>
            <Button
              onClick={submitConfirmDialog}
              disabled={confirmDialog?.kind === 'reject' && !dialogReason.trim()}
              className={confirmDialog?.kind === 'delete' ? 'bg-rose-500 hover:bg-rose-400 text-white' : 'bg-rose-400 hover:bg-rose-300 text-slate-900'}
            >
              {confirmDialog?.kind === 'delete' ? 'Delete' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
